import React, { useCallback } from 'react';

import { LinkList } from '@eika/link-list';

import { useCard } from '@/react-query/hooks/query/use-card';
import useCardRules from '@/react-query/hooks/query/use-card-rules';

import { useLinkProducer } from '@domain/app';
import { CardRegion, RegionRule } from '@generated-types';
import useText, { KortbetjeningText } from '@hooks/use-text';
import { getCardRulesUrl } from '@utils/url/get-card-details-url-utils';

const getPrettyRegionName = (region: CardRegion, texts: KortbetjeningText): string => {
    const dictionary = {
        Region1: texts.regionNorway,
        Region2: texts.regionScandinavia,
        Region3: texts.regionEurope,
        Region4: texts.regionAfrica,
        Region5: texts.regionAsia,
        Region6: texts.regionNorthAmerica,
        Region7: texts.regionSouthAmerica,
        Region8: texts.regionOceania,
    };

    return dictionary[region.id];
};

const outsideOfNorway = (region: RegionRule) => region.region.id !== 'Region1';

const CardRulesLink = () => {
    const text = useText();
    const { data: card } = useCard();
    const { data: cardRules } = useCardRules();
    const linkProducer = useLinkProducer();

    const getDescription = useCallback(() => {
        if (typeof cardRules === 'undefined') {
            return undefined;
        }

        if (cardRules.regions.every((region) => region.enabled)) {
            return text.cardControlActiveAll;
        }

        const allowedRegions = cardRules.regions.filter(({ enabled }) => enabled);

        const prettyAllowedRegions = allowedRegions
            .filter(outsideOfNorway)
            .map(({ region }) => `${getPrettyRegionName(region, text)}`)
            .join(', ');

        return `${text.cardControlActiveIn} ${prettyAllowedRegions}`;
    }, [cardRules, text]);

    if (typeof card === 'undefined') {
        return null;
    }

    return (
        <LinkList>
            <LinkList.Item href={getCardRulesUrl(card)(linkProducer)} description={getDescription()}>
                {text.spendingLimitsRegionsLabel}
            </LinkList.Item>
        </LinkList>
    );
};

export default CardRulesLink;
